import { useEffect, useState } from 'react';
import type { Problem } from '../api/types';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { EmptyState } from '../components/EmptyState';
import { ProblemNotice } from '../components/ProblemNotice';
import { Spinner } from '../components/Spinner';
import { usePlatform } from '../app/platform';
import { toProblem, useAsync } from '../app/useAsync';
import { useStudioEvents } from '../app/useStudioEvents';
import { formatDateTime } from '../lib/datetime';
import { Markdown } from '../lib/markdown';

const CONFLICT_MESSAGE = 'ほかの変更で会話まとめが進みました。最新を読み込んでから編集し直してください。';

/**
 * 組み込みの studio ソースの会話まとめ（Workspace に 1 件。C21）。AI が knowledge_write_summary で書き、利用者も編集できる。
 * 保存は編集を始めた時点の版を If-Match に付け、進んでいれば 409 version_conflict（C22）。利用者の編集は見出しを検査しない（C23）。
 */
export function ConversationSummaryPanel({ workspaceId }: { workspaceId: string }) {
  const { api } = usePlatform();
  const summary = useAsync(async () => {
    const page = await api.listKnowledgeItems(workspaceId, { category: 'conversation', limit: 1 });
    const item = page.items[0] ?? null;
    const text = item === null ? '' : new TextDecoder().decode((await api.getKnowledgeOriginal(workspaceId, item.id)).bytes);
    return { item, text, eventCursor: page.eventCursor };
  }, [api, workspaceId]);
  const [editing, setEditing] = useState<{ version: number; value: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [problem, setProblem] = useState<Problem | null>(null);

  const cursor = summary.data?.eventCursor ?? null;
  const streamState = useStudioEvents({
    api,
    workspaceId,
    cursor,
    // 編集中は取り直さない（書きかけを消さない。進んでいれば保存の 409 で分かる）。
    enabled: cursor !== null && editing === null,
    onEvent: (event) => {
      if (event.type === 'studio.knowledge' && event.action === 'summary_updated') summary.reload();
    },
  });
  useEffect(() => {
    if (streamState === 'expired') summary.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamState]);

  const stopEditing = () => {
    setEditing(null);
    setConflict(false);
    setProblem(null);
  };

  const save = async () => {
    const item = summary.data?.item;
    if (!editing || !item) return;
    setSaving(true);
    setConflict(false);
    setProblem(null);
    try {
      await api.updateConversationSummary(workspaceId, item.id, editing.value, editing.version);
      setEditing(null);
      summary.reload();
    } catch (error) {
      const failure = toProblem(error);
      if (failure.code === 'version_conflict') setConflict(true);
      else setProblem(failure);
    } finally {
      setSaving(false);
    }
  };

  if (summary.loading && summary.data === null) return <Spinner label="会話まとめを取得しています" />;
  const item = summary.data?.item ?? null;

  return (
    <Card
      title="会話まとめ"
      actions={
        item !== null && editing === null ? (
          <Button onClick={() => setEditing({ version: item.version, value: summary.data?.text ?? '' })}>編集する</Button>
        ) : undefined
      }
    >
      <div className="stack">
        <p className="hint">AI との会話で決まったことを要約した文書です。AI が更新し、利用者も編集できます。編集は利用者の決定として扱われ、AI は次の提案でこの内容を優先します。</p>
        <ProblemNotice problem={summary.problem} action={<Button onClick={summary.reload}>再取得</Button>} />
        {summary.data !== null && item === null && (
          <EmptyState title="会話まとめはまだありません" description="Studio の AI が会話で決まったことをまとめると、ここに出ます。" />
        )}
        {item !== null && <p className="hint">{`版 ${item.version} · 最終更新 ${formatDateTime(item.updatedAt)}`}</p>}
        {conflict && (
          <div className="form-conflict" role="alert">
            <span>{CONFLICT_MESSAGE}</span>
            <div className="row">
              <Button
                onClick={() => {
                  stopEditing();
                  summary.reload();
                }}
              >
                最新を読み込む
              </Button>
            </div>
          </div>
        )}
        <ProblemNotice problem={problem} />
        {item !== null && editing !== null ? (
          <>
            <textarea
              className="field-input studio-summary-editor"
              aria-label="会話まとめを編集"
              value={editing.value}
              disabled={saving}
              rows={18}
              onChange={(event) => {
                const value = event.target.value;
                setEditing((current) => (current ? { ...current, value } : current));
              }}
            />
            <div className="row">
              <Button variant="primary" disabled={saving} onClick={() => void save()}>
                保存する
              </Button>
              <Button disabled={saving} onClick={stopEditing}>
                編集をやめる
              </Button>
            </div>
          </>
        ) : (
          item !== null && <Markdown className="studio-summary" text={summary.data?.text ?? ''} />
        )}
      </div>
    </Card>
  );
}
